import { useEffect, useState } from 'react'

import { type ActivityType, type LessonSummary } from '@/platform/learning/contract'
import { type LearningService } from '@/platform/learning/service'

import { MockBadge } from './MockBadge'
import { SampleContentLabel } from './SampleContentLabel'

/**
 * Today's lesson (UI v0.2 §4.2): the lesson from `LearningService` with its
 * real activity labels, size and a single start action. When the service has
 * no lesson — always the case in pilot builds (decision D-02) — it says so
 * instead of showing anything invented. Used on Child Home and on the English
 * subject page.
 */

const ACTIVITY_LABELS: Readonly<Record<ActivityType, string>> = {
  text_choice: 'Chọn đáp án',
  picture_choice: 'Chọn hình',
  matching: 'Nối',
  word_order: 'Sắp xếp từ',
  gap_fill: 'Điền từ',
  listen_respond: 'Nghe',
  read_and_answer: 'Đọc hiểu',
  read_aloud: 'Đọc to',
  speaking_prompt: 'Nói',
}

export interface TodayLessonProps {
  readonly learning: LearningService
  readonly learnerId: string
  /** The lesson the child left mid-session, if any: offers to continue it. */
  readonly inProgressLessonId: string | null
  readonly starting: boolean
  readonly startFailed: boolean
  readonly onStart: (lesson: LessonSummary) => void
}

type LessonState =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'ready'; lesson: LessonSummary | null }

export function TodayLesson({
  learning,
  learnerId,
  inProgressLessonId,
  starting,
  startFailed,
  onStart,
}: TodayLessonProps): React.JSX.Element {
  const [state, setState] = useState<LessonState>({ kind: 'loading' })
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    let active = true
    learning.getTodayLesson(learnerId).then(
      (lesson) => {
        if (active) setState({ kind: 'ready', lesson })
      },
      () => {
        if (active) setState({ kind: 'error' })
      },
    )
    return () => {
      active = false
    }
  }, [learning, learnerId, reloadKey])

  return (
    <>
      {state.kind === 'loading' && (
        <p role="status" className="text-lg text-slate-700 dark:text-slate-300">
          Đang tải bài học…
        </p>
      )}

      {state.kind === 'error' && (
        <div className="flex flex-col items-center gap-3">
          <p role="alert" className="text-lg">
            Chưa tải được bài học. Con thử lại nhé.
          </p>
          <button
            type="button"
            className="h-12 rounded-xl bg-sky-700 px-6 text-lg font-semibold text-white"
            onClick={() => {
              setState({ kind: 'loading' })
              setReloadKey((k) => k + 1)
            }}
          >
            Thử lại
          </button>
        </div>
      )}

      {state.kind === 'ready' && state.lesson === null && (
        <p className="max-w-xs text-lg text-slate-700 dark:text-slate-300">
          Hôm nay chưa có bài học nào. Khi có bài mới, bài sẽ hiện ở đây.
        </p>
      )}

      {state.kind === 'ready' && state.lesson !== null && (
        <LessonCard
          lesson={state.lesson}
          resuming={state.lesson.lesson_id === inProgressLessonId}
          starting={starting}
          startFailed={startFailed}
          onStart={onStart}
        />
      )}
    </>
  )
}

function LessonCard({
  lesson,
  resuming,
  starting,
  startFailed,
  onStart,
}: {
  lesson: LessonSummary
  resuming: boolean
  starting: boolean
  startFailed: boolean
  onStart: (lesson: LessonSummary) => void
}): React.JSX.Element {
  const minutes = Math.max(1, Math.ceil(lesson.estimated_seconds / 60))
  return (
    <section
      aria-labelledby="today-lesson"
      className="flex w-full flex-col gap-4 rounded-3xl border-2 border-sky-200 bg-white p-5 text-left shadow-sm dark:border-sky-900 dark:bg-slate-900"
    >
      <div className="flex items-center justify-between gap-2">
        <p className="text-base font-semibold text-sky-800 dark:text-sky-300">
          Bài học hôm nay
        </p>
        {lesson.is_mock && <MockBadge />}
      </div>
      <h2 id="today-lesson" lang="en" className="text-2xl font-bold">
        {lesson.title}
      </h2>
      {lesson.is_sample_content && <SampleContentLabel />}
      <ul aria-label="Dạng bài" className="flex flex-wrap gap-2">
        {lesson.activity_types.map((type) => (
          <li
            key={type}
            className="rounded-full bg-sky-100 px-3 py-1 text-base text-sky-950 dark:bg-sky-950 dark:text-sky-100"
          >
            {ACTIVITY_LABELS[type]}
          </li>
        ))}
      </ul>
      <p className="text-base text-slate-700 dark:text-slate-300">
        {lesson.item_count} câu · khoảng {minutes} phút
      </p>
      {startFailed && (
        <p role="alert" className="text-base text-rose-700 dark:text-rose-400">
          Chưa mở được bài học. Con thử lại nhé.
        </p>
      )}
      <button
        type="button"
        disabled={starting}
        className="h-14 rounded-2xl bg-sky-700 text-lg font-semibold text-white active:bg-sky-800 disabled:opacity-40"
        onClick={() => {
          onStart(lesson)
        }}
      >
        {starting ? 'Đang mở…' : resuming ? 'Học tiếp' : 'Bắt đầu'}
      </button>
      {lesson.is_mock && (
        <p className="text-base text-slate-600 dark:text-slate-400">
          Bài học mẫu để thử ứng dụng, không phải bài học thật.
        </p>
      )}
    </section>
  )
}
